// -----------------------------------------------------------------------------
// Catches an Excalidraw library install on its way in.
//
// "Add to Excalidraw" on libraries.excalidraw.com sends the browser back to
// libraryReturnUrl with an `#addLibrary=<url>&token=<t>` hash. Two things make
// that awkward to handle where it is used:
//
//   - The editor is only mounted when a diagram is open, and the return may
//     land on any page. Nothing would read the hash.
//   - React Router rewrites the location as soon as it mounts, so a hash left
//     for later is gone before any component can ask for it.
//
// So the hash is read at module load -- before React starts -- and parked in
// sessionStorage until a free-form editor is ready to install it.
// -----------------------------------------------------------------------------

const KEY = "schemunda:pending-library-url";

/** Reads `#addLibrary` out of the current URL and stores it. */
function capture(): void {
  try {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const url = hash.get("addLibrary");
    if (!url) return;

    sessionStorage.setItem(KEY, url);

    // Strip it so a reload does not install twice, keeping any other hash keys.
    hash.delete("addLibrary");
    hash.delete("token");
    const rest = hash.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}${rest ? `#${rest}` : ""}`,
    );
  } catch {
    // Private mode, blocked storage, or a malformed hash. An install that does
    // not happen is an inconvenience; a crash on startup is not.
  }
}

capture();

// The install can also arrive while the app is already open, if the library
// site reuses this tab.
window.addEventListener("hashchange", capture);

/** Returns a pending library URL exactly once, clearing it. */
export function takePendingLibraryUrl(): string | null {
  try {
    const url = sessionStorage.getItem(KEY);
    if (url) sessionStorage.removeItem(KEY);
    return url;
  } catch {
    return null;
  }
}

/** True when an install is waiting for an editor to open. */
export function hasPendingLibrary(): boolean {
  try {
    return sessionStorage.getItem(KEY) !== null;
  } catch {
    return false;
  }
}
